import React, { useCallback, useContext, useMemo, useState } from 'react';
import AddIcon from '@material-ui/icons/Add';
import {
  DRAWER_REGISTRY_MAP,
  DrawerStack,
  SendoutsDrawerStackProvider,
  useSendoutsDrawerHandlers
} from 'features/sendouts';
import { PROCESS_TYPE } from 'features/sendouts/constants/constants';
import { UserProviderContext } from 'providers/UserProvider';
import { canUserOrStaffEditEntity } from 'services/Authorization';
import strings from 'strings';
import { componentDimensions } from 'UI/constants/dimensions';
import { EntityType, TabKeys } from 'UI/constants/entityTypes';
import { SvgNoSendouts } from 'UI/res/icons/milano';
import { sendoutsFormatResponse } from 'UI/utils/inventory';

import ProfileTableLayout from '../ProfileTableLayout';

const {
  sendouts: STRINGS,
  inventoryProfiles: {
    emptyStates: { sendouts: sendoutsEmptyStrings }
  }
} = strings;

const { emptyState } = componentDimensions;

const SendoutsTabContent = ({
  canUseSendout,
  DataTableProps,
  extraRenderers,
  initialColumns,
  onCreateSendout,
  onCreateSendover,
  onNewItemClick,
  onViewSendout,
  paramsBuilder,
  profile,
  profileModule,
  refreshSignal = 0,
  tablePreferencesKey,
  ...rest
}) => {
  const isJobOrderMode = profileModule === EntityType.Joborder;
  const isSendoutActionDisabled = !canUseSendout || !profile?.id;

  const actionButtonProps = { disabled: isSendoutActionDisabled };
  const finalActions = [
    {
      name: 'view',
      callback: onViewSendout
    }
  ];

  const actionsDefinition = useMemo(
    () => [
      {
        props: {
          text: STRINGS.tab.actions.createSendout,
          variant: 'contained',
          onClick: onCreateSendout,
          icon: AddIcon,
          disabled: isSendoutActionDisabled
        },
        GridItemProps: {
          md: 'auto'
        }
      },
      {
        props: {
          text: STRINGS.tab.actions.createSendover,
          variant: 'outlined',
          onClick: onCreateSendover,
          icon: AddIcon,
          disabled: isSendoutActionDisabled
        },
        GridItemProps: {
          md: 'auto'
        }
      }
    ],
    [isSendoutActionDisabled, onCreateSendout, onCreateSendover]
  );

  return (
    <ProfileTableLayout
      actionButtonProps={actionButtonProps}
      actionText={STRINGS.tab.actions.createSendout}
      customEmptyState={<SvgNoSendouts size={emptyState.width} />}
      customActionsDefinitions={actionsDefinition}
      extraRenderers={extraRenderers}
      finalActions={finalActions}
      hasProfileLoaded={profile?.id}
      initialColumns={initialColumns}
      onNewItemClick={isJobOrderMode ? onNewItemClick : null}
      profileModule={profileModule}
      shouldRefresh={refreshSignal}
      tabKey={TabKeys.Sendouts}
      paramsBuilder={paramsBuilder}
      orderByOptions={{ column: 'sent_on', direction: 'desc' }}
      tablePreferencesKey={tablePreferencesKey}
      DataTableProps={{ formatResponse: sendoutsFormatResponse, ...DataTableProps }}
      {...sendoutsEmptyStrings}
      {...rest}
    />
  );
};

const SendoutsTabV2Container = props => {
  const { profile } = props;
  const [refreshVersion, setRefreshVersion] = useState(0);

  const handleDrawerSaveSuccess = useCallback(({ effects }) => {
    if (effects?.refreshTable) {
      setRefreshVersion(currentVersion => currentVersion + 1);
    }
  }, []);

  const {
    onDrawerClose,
    onDrawerSaved,
    openCreateSendout,
    openCreateSendover,
    openSendoutDetails,
    openSendoverDetails,
    stack
  } = useSendoutsDrawerHandlers({
    jobOrder: profile,
    onSubmitSuccess: handleDrawerSaveSuccess
  });

  const handleViewSendout = useCallback(
    item => {
      if (!item || !item.type) return;

      if (item.type?.id === PROCESS_TYPE.Sendout.id) {
        openSendoutDetails({ sendoutId: item.id });
      } else if (item.type?.id === PROCESS_TYPE.Sendover.id) {
        openSendoverDetails({ sendoverId: item.id });
      }
    },
    [openSendoutDetails, openSendoverDetails]
  );

  return (
    <>
      <SendoutsTabContent
        {...props}
        refreshSignal={refreshVersion}
        onCreateSendout={openCreateSendout}
        onCreateSendover={openCreateSendover}
        onViewSendout={handleViewSendout}
      />
      <DrawerStack
        stack={stack}
        registry={DRAWER_REGISTRY_MAP}
        onClose={onDrawerClose}
        onSaved={onDrawerSaved}
      />
    </>
  );
};

const SendoutsTabV2 = props => {
  const { profile, profileModule } = props;
  const [currentUser] = useContext(UserProviderContext);
  const canUseSendout = canUserOrStaffEditEntity(currentUser, profile);
  const isJobOrderMode = profileModule === EntityType.Joborder;

  if (!isJobOrderMode) {
    return <SendoutsTabContent {...props} canUseSendout={canUseSendout} />;
  }

  return (
    <SendoutsDrawerStackProvider>
      <SendoutsTabV2Container {...props} canUseSendout={canUseSendout} />
    </SendoutsDrawerStackProvider>
  );
};

export default SendoutsTabV2;
